import React, { useContext, useState, useEffect, ChangeEvent } from "react";
import {
  Table,
  Tag,
  Button,
  message,
  Row,
  Col,
  Card,
  Spin,
  DatePicker,
  Modal,
  Input,
  Select,
  Tooltip,
  Pagination,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import {
  addAttendance,
  getallAttendance,
  getallDeveloper,
  getallDeveloperForTask,
  getallRequest,
  getallUserAttendance,
  getAllUserAttendanceExport,
  updateRequest,
  getDeveloperByID,
} from "../../utils/API";
import { AuthContext } from "../../Auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaPen } from "react-icons/fa";
import moment from "moment";
import {
  BsPersonCheck,
  BsPersonX,
  BsPersonDash,
  BsCalendarX,
} from "react-icons/bs";
import { getUserInfo } from "../../utils/Constants";
import type { SorterResult, SortOrder } from "antd/es/table/interface";
import * as XLSX from "xlsx";
import Editattendancemodal from "./Edit";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // default theme
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

const { MonthPicker } = DatePicker;
const { TextArea } = Input;
const { Option } = Select;

interface AttendanceRecord {
  dayCount: any;
  _id?: string;
  date: string;
  day?: string; // ✅ Added day property
  clockIn?: string;
  lunchStart?: string;
  lunchEnd?: string;
  clockOut?: string;
  status?: string;
  taskReport?: string;
  userId: {
    _id: string;
    username: string;
  };
  username?: string;
  developerNumber?: string; // ✅ Add developer number
  endTime?: string; // ✅ Added total time field
  requestStatus?: string;
  highlightRed?: boolean;
  ipAddress?: string;
}

interface Developer {
  _id: string;
  username: string;
  developerNumber: string;
}
interface EditRequest {
  _id: string;
  attendanceId: { _id: string };
  status: string;
}
const AttendanceTable = () => {
  const allowedIP = "103.86.16.203";
  const { authData } = useContext(AuthContext);
  const navigate = useNavigate();
  const userId = authData?.user?._id;
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false); // Add this line
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AttendanceRecord[]>([]);
  const [filteredData, setFilteredData] = useState<AttendanceRecord[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format("YYYY-MM"));
  const [remainingMinutes, setRemainingMinutes] = useState(480);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [taskReport, setTaskReport] = useState("");
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState<AttendanceRecord | null>(null);
  const [developers, setDevelopers] = useState<Developer[]>([]);
  const [selectedDeveloper, setSelectedDeveloper] = useState<string | null>(
    null,
  );
  const todayDate = new Date().getDate();
  const [lunchPauseTime, setLunchPauseTime] = useState<Dayjs | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(todayDate);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [selectedMonthNum, setSelectedMonthNum] = useState(dayjs().month() + 1); // Months are 0-indexed in dayjs
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(10);
  const [lunchStartTime, setLunchStartTime] = useState<Dayjs | null>(null);
  const [workingMinutesBeforeLunch, setWorkingMinutesBeforeLunch] = useState(0);
  const [breakMinutesElapsed, setBreakMinutesElapsed] = useState(0);
  const [filteredDevelopers, setFilteredDevelopers] = useState<Developer[]>([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [sortField, setSortField] = useState<string>("date"); // default sort by date
  const [sortOrder, setSortOrder] = useState<SortOrder>("descend"); // default descending
  const [pageSize, setPageSize] = useState(40);
  const pageSizeOptions = ["10", "20", "40", "50", "100"];
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: SortOrder;
  }>({
    field: "date",
    direction: "descend",
  });
  const date = new Date(); // or whatever date value you need
  const [buttonState, setButtonState] = useState({
    clockIn: false,
    lunchStart: true,
    lunchComplete: true,
    clockOut: true,
  });

  // Mandatory break duration in minutes
  const MANDATORY_BREAK_MINUTES = 30;

  // Whether the logged-in developer is excluded from the mandatory
  // 30-minute lunch break cycle (configured per-developer via is_exclude).
  const [isExcludedFromBreak, setIsExcludedFromBreak] = useState(false);

  useEffect(() => {
    const checkBreakExclusion = async () => {
      if (!userId) return;
      try {
        const res: any = await getDeveloperByID(userId);
        setIsExcludedFromBreak(res?.is_exclude === true);
      } catch {
        setIsExcludedFromBreak(false);
      }
    };
    checkBreakExclusion();
  }, [userId]);

  const isAdmin = authData?.roleId?.roleId === 1;
  const attendancePermission = authData?.permissions?.find(
    (p: any) => p?.menuId?.slug === "attendances",
  );
  const hasAllAttendanceAccess =
    attendancePermission?.permissionIds?.attendanceAll === true ||
    attendancePermission?.permissionIds?.all === true;

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    // Handle pagination changes
    if (pagination.current !== page) {
      setPage(pagination.current);
    }
    // Handle sorting changes
    if (sorter.field) {
      setSortConfig({
        field: sorter.field,
        direction: sorter.order,
      });
      fetchAttendanceData(sorter.field, sorter.order);
    }
  };

  type StatusType =
    | "Present"
    | "Absent"
    | "HalfDay"
    | "Leave"
    | "Sunday"
    | "Holiday"
    | "WeekOff"
    | "HalfLeave";

  const statusStyles = {
    Present: {
      background: "#dcfce7", // light green
      text: "#15803d", // dark green
    },
    Absent: {
      background: "#fee2e2", // light red
      text: "#b91c1c", // dark red
    },
    HalfDay: {
      background: "#f3e8ff", // very light lavender
      text: "#6b21a8", // deep violet
    },

    Leave: {
      background: "#fef9c3", // light yellow
      text: "#ca8a04", // amber
    },
    Sunday: {
      background: "#ede9fe", // light purple
      text: "#7c3aed", // indigo
    },
    Holiday: {
      background: "#fce7f3", // light pink
      text: "#db2777", // pink
    },
    WeekOff: {
      background: "#ffedd5", // light orange
      text: "#ea580c", // orange
    },
    HalfLeave: {
      background: "#15803d", // light green
      text: "#dcfce7", // dark green
    },
  };

  useEffect(() => {
    if (authData?.roleId?.roleId === 1) {
      const fetchDevelopers = async () => {
        try {
          const result = await getallDeveloperForTask("", "");
          setDevelopers(result.data);
          setFilteredDevelopers(result.data);
        } catch (error) {
          message.error("Failed to fetch developers.");
        }
      };
      fetchDevelopers();
    }
  }, [authData, selectedDeveloper]);

  const STORAGE_KEY = "attendance_filters";

  // Load filters from local storage on component mount
  // const saveFiltersToStorage = () => {
  //   const filters = {
  //     month: selectedMonthNum,
  //     year: selectedYear,
  //     day: selectedDay,
  //     developer: selectedDeveloper
  //   };
  //   localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  // };

  const loadFiltersFromStorage = () => {
    const savedFilters = localStorage.getItem(STORAGE_KEY);
    if (savedFilters) {
      const { month, year, day, developer } = JSON.parse(savedFilters);
      if (month) setSelectedMonthNum(month);
      if (year) setSelectedYear(year);
      if (day) setSelectedDay(day);
      if (developer) setSelectedDeveloper(developer);
    }
  };

  useEffect(() => {
    loadFiltersFromStorage();
  }, []);

  const fetchAttendanceData = async (
    sortField: string = sortConfig.field,
    sortOrder: SortOrder = sortConfig.direction,
  ) => {
    try {
      setLoading(true);
      const currentIP = await getUserInfo();
      const ipMismatch = currentIP !== allowedIP;
      let response;

      const currentDate = dayjs();
      const effectiveMonth = selectedMonthNum || currentDate.month() + 1;
      const effectiveYear = selectedYear || currentDate.year();

      // Prepare sort parameters for API
      const sortParam = sortField || "";
      const sortDirection = sortOrder
        ? sortOrder === "ascend"
          ? "asc"
          : "desc"
        : "";

      // ✅ Allow either admin or permission flag to access all-user attendance
      if (isAdmin || hasAllAttendanceAccess) {
        response = await getallUserAttendance(
          selectedDeveloper,
          page,
          pageSize,
          selectedDay,
          effectiveMonth,
          effectiveYear,
          sortParam,
          sortDirection,
        );
        setTotalRecords(response.total);
      } else {
        response = await getallAttendance(
          page,
          pageSize,
          effectiveMonth,
          effectiveYear,
          sortParam,
          sortDirection,
        );
        setTotalRecords(response.total);
      }

      let attendanceRecords: AttendanceRecord[] = response.data;

      // client-side sort fallback
      if (!sortParam) {
        attendanceRecords.sort((a, b) => {
          const dateA = dayjs(a.date);
          const dateB = dayjs(b.date);
          return dateB.diff(dateA);
        });
      }

      // add serial numbers
      attendanceRecords = attendanceRecords.map((attendance, index) => ({
        ...attendance,
        serialNumber: index + 1,
      }));

      // filter by selectedDay for admin or all-access users
      if ((isAdmin || hasAllAttendanceAccess) && selectedDay !== null) {
        attendanceRecords = attendanceRecords.filter((r) => {
          const date = dayjs(r.date);
          return (
            date.month() + 1 === effectiveMonth &&
            date.year() === effectiveYear &&
            date.date() === selectedDay
          );
        });
      }

      // today's record logic remains unchanged
      const todayDate = moment().format("YYYY-MM-DD");
      const todayRecord = attendanceRecords.find(
        (item: AttendanceRecord) => item.date === todayDate,
      );

      if (todayRecord) {
        setButtonState({
          clockIn: !!todayRecord.clockIn,
          lunchStart: !todayRecord.clockIn || !!todayRecord.lunchStart,
          lunchComplete: !todayRecord.lunchStart || !!todayRecord.lunchEnd,
          clockOut: !todayRecord.lunchEnd || !!todayRecord.clockOut,
        });
        if (todayRecord.lunchStart && !todayRecord.lunchEnd) {
          const elapsed = dayjs().diff(
            dayjs(todayRecord.lunchStart, "HH:mm"),
            "minute",
          );
          setBreakMinutesElapsed(elapsed);
        }

        if (todayRecord.clockIn && !todayRecord.clockOut) {
          const clockInTime = dayjs(todayRecord.clockIn, "HH:mm");
          const now = dayjs(todayRecord.lunchStart || undefined);
          const elapsedMinutes = now.diff(clockInTime, "minute");
          const remaining = Math.max(480 - elapsedMinutes, 0);
          setRemainingMinutes(remaining);
          startTimer();
        }

        if (!todayRecord.lunchEnd && todayRecord.lunchStart) {
          setIsPaused(true);
        }
      }

      const attendanceRecordsWithIP = attendanceRecords
        //@ts-ignore
        .filter((record) => record.visible)
        .map((record) => ({
          ...record,
          highlightRed: ipMismatch,
        }));

      setData(attendanceRecordsWithIP);
      setFilteredData(attendanceRecordsWithIP);
    } catch (error) {
      message.error("Failed to fetch attendance data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authData?.user?._id) {
      fetchAttendanceData();
    }
  }, [
    authData,
    userId,
    selectedDeveloper,
    page,
    selectedMonthNum,
    selectedYear,
    selectedDay,
  ]);

  // Update the handleFilter function to properly handle userId comparison
  const handleFilter = () => {
    if (!data || !authData) return;

    const filtered = data.filter((record) => {
      const matchesMonth = selectedMonth
        ? record.date?.startsWith(selectedMonth)
        : true;

      // Handle both string and object userId cases
      const recordUserId = record.userId?._id || record.userId;

      const matchesDeveloper =
        authData?.roleId?.roleId === 1 && selectedDeveloper
          ? recordUserId === selectedDeveloper
          : true;

      return matchesMonth && matchesDeveloper;
    });

    setFilteredData(filtered);
  };

  useEffect(() => {
    handleFilter();
  }, [selectedMonth, selectedDeveloper, data, authData]);
  // Update the state variables at the top of your component
  // Live tracker for elapsed break time (updates while on break)
  useEffect(() => {
    const today = data.find((item: any) => item.date === dayjs().format("YYYY-MM-DD"));
    if (today && today.lunchStart && !today.lunchEnd) {
      const update = () => {
        const start = dayjs(today.lunchStart, "HH:mm");
        setBreakMinutesElapsed(dayjs().diff(start, "minute"));
      };
      update();
      const interval = setInterval(update, 30000);
      return () => clearInterval(interval);
    } else {
      setBreakMinutesElapsed(0);
    }
  }, [data]);

  // const [lunchStartTime, setLunchStartTime] = useState<string | null>(null);

  // Modify the handleClockAction function
  const handleClockAction = async (
    action: keyof AttendanceRecord,
    taskReport?: string,
  ) => {
    // Immediately disable the clicked button
    if (action === "clockIn") {
      setButtonState((prev) => ({ ...prev, clockIn: true }));
    } else if (action === "lunchStart") {
      setButtonState((prev) => ({ ...prev, lunchStart: true }));
    } else if (action === "lunchEnd") {
      // Enforce mandatory 30-minute break
      const existingRecord = data.find((item: any) => item.date === dayjs().format("YYYY-MM-DD"));
      if (existingRecord && existingRecord.lunchStart) {
        const breakStart = dayjs(existingRecord.lunchStart, "HH:mm");
        const breakElapsed = dayjs().diff(breakStart, "minute");
        if (!isExcludedFromBreak && breakElapsed < MANDATORY_BREAK_MINUTES) {
          const remaining = MANDATORY_BREAK_MINUTES - breakElapsed;
          message.warning(
            `Break is mandatory for ${MANDATORY_BREAK_MINUTES} minutes. ${remaining} minute(s) remaining.`,
          );
          return;
        }
      }
      setButtonState((prev) => ({ ...prev, lunchComplete: true }));
    } else if (action === "clockOut") {
      setButtonState((prev) => ({ ...prev, clockOut: true }));
    }

    if (!userId) {
      message.error("User not authenticated.");
      return;
    }

    const now = dayjs().format("HH:mm");
    const todayDate = dayjs().format("YYYY-MM-DD");
    const ipAddress = await getUserInfo();
    let todayRecord: any = data.find((item: any) => item.date === todayDate);

    if (!todayRecord) {
      todayRecord = {
        date: todayDate,
        day: todayDate,
        clockIn: "",
        lunchStart: "",
        lunchEnd: "",
        clockOut: "",
        taskReport: "",
        status: "",
        userId,
        ipAddress,
        endTime: "Remaining: 8h 0m",
      };
    }

    let updatedRecord = { ...todayRecord, ipAddress, [action]: now };

    // Handle different clock actions
    if (action === "clockIn") {
      setIsPaused(false);
      setLunchStartTime(null);
      setWorkingMinutesBeforeLunch(0);
      startTimer();
    }

    if (action === "lunchStart") {
      setIsPaused(true);
      setLunchStartTime(dayjs(now, "HH:mm"));
      if (updatedRecord.clockIn) {
        const clockInTime = dayjs(updatedRecord.clockIn, "HH:mm");
        const lunchStart = dayjs(now, "HH:mm");
        setWorkingMinutesBeforeLunch(lunchStart.diff(clockInTime, "minute"));
      }
      stopTimer();
    }

    if (action === "lunchEnd") {
      setIsPaused(false);
      setLunchStartTime(null);
      startTimer();
    }

    if (action === "clockOut") {
      stopTimer();

      let totalMinutes = 0;
      if (updatedRecord.clockIn && updatedRecord.clockOut) {
        const clockInTime = dayjs(updatedRecord.clockIn, "HH:mm");
        const clockOutTime = dayjs(updatedRecord.clockOut, "HH:mm");

        // Total worked minutes from clockIn to clockOut
        let totalWorked = clockOutTime.diff(clockInTime, "minute");

        // Subtract lunch duration if available
        if (updatedRecord.lunchStart && updatedRecord.lunchEnd) {
          const lunchStart = dayjs(updatedRecord.lunchStart, "HH:mm");
          const lunchEnd = dayjs(updatedRecord.lunchEnd, "HH:mm");
          const lunchDuration = lunchEnd.diff(lunchStart, "minute");
          totalWorked -= lunchDuration;
        }

        totalMinutes = totalWorked;
      }

      if (taskReport) {
        updatedRecord.taskReport = taskReport;
      }

      const workedHours = Math.floor(totalMinutes / 60);
      const workedMinutes = totalMinutes % 60;
      updatedRecord.endTime = `${workedHours}h ${workedMinutes}m`;
    }

    try {
      await addAttendance(updatedRecord);
      fetchAttendanceData();
      message.success("Attendance recorded successfully!");

      // Update local state
      setData((prevData: any) => {
        const existingIndex = prevData.findIndex(
          (item: any) => item.date === todayDate,
        );
        if (existingIndex !== -1) {
          const newData = [...prevData];
          newData[existingIndex] = updatedRecord;
          return newData;
        } else {
          return [...prevData, updatedRecord];
        }
      });

      // Update button states
      const newState = { ...buttonState };
      switch (action) {
        case "clockIn":
          newState.clockIn = true;
          newState.lunchStart = false;
          break;
        case "lunchStart":
          newState.lunchStart = true;
          newState.lunchComplete = false;
          break;
        case "lunchEnd":
          newState.lunchComplete = true;
          newState.clockOut = false;
          break;
        case "clockOut":
          newState.clockOut = true;
          break;
        default:
          break;
      }

      setButtonState(newState);
    } catch (error) {
      message.error("Failed to record attendance. Please try again.");
    }
  };
  // Update the startTimer function
  const startTimer = () => {
    if (timer) {
      clearInterval(timer);
      setTimer(null);
    }

    if (buttonState.clockIn && !buttonState.clockOut && !isPaused) {
      const interval = setInterval(() => {
        setRemainingMinutes((prev) => {
          const todayRecord = data.find(
            (item) => item.date === dayjs().format("YYYY-MM-DD"),
          );
          if (!todayRecord?.clockIn) return prev;

          const clockInTime = dayjs(todayRecord.clockIn, "HH:mm");
          const now = dayjs();

          let totalWorked = 0;

          if (todayRecord.lunchStart && todayRecord.lunchEnd) {
            // Time before lunch
            const lunchStart = dayjs(todayRecord.lunchStart, "HH:mm");
            const beforeLunch = lunchStart.diff(clockInTime, "minute");

            // Time after lunch
            const lunchEnd = dayjs(todayRecord.lunchEnd, "HH:mm");
            const afterLunch = now.diff(lunchEnd, "minute");

            totalWorked = beforeLunch + afterLunch;
          } else {
            // No lunch taken yet
            totalWorked = now.diff(clockInTime, "minute");
          }

          return Math.max(480 - totalWorked, 0);
        });
      }, 60000);

      setTimer(interval);
    }
  };
  // Removed duplicate declaration of stopTimer
  const stopTimer = () => {
    if (timer) {
      clearInterval(timer);
      setTimer(null);
    }
  };
  const formatRemainingTime = (record: any) => {
    if (!record?.clockIn) return "Remaining: 8h 0m";

    const clockInTime = dayjs(record.clockIn, "HH:mm");
    const now = dayjs();

    let totalWorked = 0;

    if (record.lunchStart && record.lunchEnd) {
      const lunchStart = dayjs(record.lunchStart, "HH:mm");
      const beforeLunch = lunchStart.diff(clockInTime, "minute");

      const lunchEnd = dayjs(record.lunchEnd, "HH:mm");
      const afterLunch = now.diff(lunchEnd, "minute");

      totalWorked = beforeLunch + afterLunch;
    } else if (record.lunchStart && !record.lunchEnd) {
      totalWorked = dayjs(record.lunchStart, "HH:mm").diff(
        clockInTime,
        "minute",
      );
    } else {
      totalWorked = now.diff(clockInTime, "minute");
    }

    const remaining = Math.max(480 - totalWorked, 0);
    const hours = Math.floor(remaining / 60);
    const minutes = remaining % 60;

    return `Remaining: ${hours}h ${minutes}m`;
  };

  const handleEdit = (record: AttendanceRecord) => {
    setCurrentTask(record); // Set the current task to edit
    setIsEditModalVisible(true); // Open the modal
    fetchAttendanceData();
  };

  // ✅ Show Clock Out Modal
  const showClockOutModal = () => {
    setIsModalVisible(true);
  };

  // ✅ Handle Modal Close
  const handleModalCancel = () => {
    setIsModalVisible(false);
    setTaskReport(""); // Reset input
  };

  // ✅ Handle Clock Out with Task Report
  const handleClockOutWithReport = () => {
    fetchAttendanceData();
    if (!taskReport.trim()) {
      message.warning("Please enter a report before clocking out.");
      return;
    }

    // ✅ Submit clock-out with report
    handleClockAction("clockOut", taskReport);
    setIsModalVisible(false);
    setTaskReport("");
  };

  const handleReportChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setTaskReport(e.target.value);
  };

  const exportToExcel = async () => {
    setExportLoading(true);
    try {
      // Call the API endpoint that returns all data at once
      const response = await getAllUserAttendanceExport(
        selectedDeveloper,
        selectedDay,
        selectedMonthNum,
        selectedYear,
      );

      if (!response.data || response.data.length === 0) {
        message.warning("No data to export");
        return;
      }

      const exportData = (response.data as AttendanceRecord[]).map((item) => ({
        "Developer Name": item.userId?.username || "-",
        Date: dayjs(item.date).format("DD-MM-YYYY"),
        Day: dayjs(item.date).format("dddd"),
        "Clock In": item.clockIn || "-",
        "Lunch Start": item.lunchStart || "-",
        "Lunch End": item.lunchEnd || "-",
        "Clock Out": item.clockOut || "-",
        "Total Time": item.endTime || "-",
        "Days Count": item.dayCount || "-",
        Status: item.status || "-",
        "IP Address": item.ipAddress || "-",
        "Task Report": item.taskReport || "-",
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Attendance");

      const monthName = dayjs()
        .month(selectedMonthNum - 1)
        .format("MMMM");

      let fileName = `Attendance_${monthName}_${selectedYear}.xlsx`;

      if (selectedDay) {
        const dateName = dayjs()
          .date(selectedDay)
          .month(selectedMonthNum - 1)
          .year(selectedYear)
          .format("DD-MM-YYYY");

        fileName = `Attendance_${dateName}.xlsx`;
      }
      XLSX.writeFile(wb, fileName);
      message.success(`Exported ${response.data.length} records`);
    } catch (error) {
      message.error("Failed to export data");
      console.error("Export error:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const isCurrentMonthYear =
    selectedMonthNum === dayjs().month() + 1 && selectedYear === dayjs().year();

  const columns: ColumnsType<AttendanceRecord> = [
    {
      title: "#", // Index Column
      key: "index",
      fixed: "left" as const,
      render: (_text, _record, index) => index + 1,
    },
    ...(isAdmin || hasAllAttendanceAccess
      ? [
          {
            title: "Developer Name",
            dataIndex: "userId",
            key: "username",
            fixed: "left" as const,
            render: (user: { username: string }) => user?.username || "-",
          },
        ]
      : []),
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      fixed: "left" as const,
      render: (date: string) => {
        const parsedDate = dayjs(date, ["YYYY-MM-DD", "DD-MM-YYYY"], true);
        return parsedDate.isValid() ? parsedDate.format("DD-MM-YYYY") : "-";
      },
      sorter: {
        compare: (a, b) => {
          const dateA = dayjs(a.date, ["YYYY-MM-DD", "DD-MM-YYYY"], true);
          const dateB = dayjs(b.date, ["YYYY-MM-DD", "DD-MM-YYYY"], true);

          if (dateA.isValid() && dateB.isValid()) {
            return dateA.unix() - dateB.unix();
          }
          return 0;
        },
        multiple: 1, // Set priority if multiple sorters
      },
      sortDirections: ["descend", "ascend"] as SortOrder[],
      defaultSortOrder: "descend" as SortOrder,
    },
    {
      title: "Day",
      dataIndex: "date",
      key: "day",
      fixed: "left" as const,
      render: (date) => dayjs(date).format("dddd"), // Shows full day name
    },
    { title: "Clock In", dataIndex: "clockIn", key: "clockIn" },
    { title: "Lunch Start", dataIndex: "lunchStart", key: "lunchStart" },
    { title: "Lunch Complete", dataIndex: "lunchEnd", key: "lunchEnd" },
    { title: "Clock Out", dataIndex: "clockOut", key: "clockOut" },
    {
      title: "Total Time",
      dataIndex: "endTime",
      key: "totalTime",
      render: (_, record) => {
        const todayDate = dayjs().format("DD-MM-YYYY");
        const recordDate = dayjs(record.date).format("DD-MM-YYYY");
        const isAdmin = authData?.roleId?.roleId === 1;

        if (recordDate === todayDate) {
          if (!record.clockOut) {
            // Show paused time during lunch break
            if (record.lunchStart && !record.lunchEnd) {
              return "Paused for lunch";
            }
            return formatRemainingTime(record);
          }
          return record.endTime || "-";
        }

        // For past records, calculate total time if not already set
        if (!record.endTime && record.clockIn && record.clockOut) {
          const clockInTime = dayjs(record.clockIn, "HH:mm");
          const clockOutTime = dayjs(record.clockOut, "HH:mm");
          let totalMinutes = clockOutTime.diff(clockInTime, "minute");

          // Subtract lunch duration if available
          if (record.lunchStart && record.lunchEnd) {
            const lunchStart = dayjs(record.lunchStart, "HH:mm");
            const lunchEnd = dayjs(record.lunchEnd, "HH:mm");
            const lunchDuration = lunchEnd.diff(lunchStart, "minute");
            totalMinutes -= lunchDuration;
          }

          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          return `${hours}h ${minutes}m`;
        }

        return record.endTime || "-";
      },
    },
    {
      title: "Day",
      key: "dayCount",
      render: (_: any, record: any) => {
        return <Tag>{record.dayCount ?? "N/A"}</Tag>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: StatusType) => {
        const style = statusStyles[status] || {
          background: "#f0f0f0",
          text: "#000",
        };

        return (
          <span
            className="badge rounded-pill"
            style={{
              background: style.background,
              color: style.text,
              fontSize: "10px",
              whiteSpace: "nowrap",
              textAlign: "center",
            }}
          >
            {status || "Unknown"}
          </span>
        );
      },
    },

    ...(authData?.roleId?.roleId === 1
      ? [
          {
            title: "IP Address",
            dataIndex: "ipAddress",
            key: "ipAddress",
            render: (ipAddress: string) => (
              <span
                style={{ color: ipAddress !== allowedIP ? "red" : "inherit" }}
              >
                {ipAddress || "-"}
              </span>
            ),
          },
        ]
      : []),

    {
      title: "Action",
      key: "action",
      render: (_: any, record: AttendanceRecord) => {
        // ✅ Always show for admin, only show for others if not Approved
        if (!isAdmin && record.requestStatus === "Approved") return null;

        return (
          <Tooltip title="Edit">
            <button
              onClick={() => handleEdit(record)}
              className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
                     shadow-[0_4px_12px_rgba(139,92,246,0.3)] 
                     hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] 
                     active:translate-y-[1px] active:shadow-none 
                     text-white flex items-center justify-center 
                     transition-all duration-200 ease-in-out group"
            >
              <FaPen
                className="group-hover:scale-110 transition-transform duration-200"
                size={12}
              />
            </button>
          </Tooltip>
        );
      },
    },
  ];

  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  const updatedData = data.map((row) => ({
    ...row,
    highlightRed: row.ipAddress !== allowedIP,
  }));

  return (
    <div>
      <Row className="m-2" gutter={[16, 16]} align="middle">
        {/* Title Column - Always first */}
        <Col xs={24} sm={24} md={4} lg={3} xl={3}>
          <h2 className="headername">Attendance</h2>
        </Col>

        {/* Admin View (roleId === 1) */}
        {authData?.roleId?.roleId === 1 && (
          <>
            {/* Developer Select - Compact */}
            <Col xs={24} sm={24} md={6} lg={5} xl={4}>
              <Select
                showSearch
                style={{ width: "100%", marginLeft: "2vw", height: "33px" }}
                placeholder="Select developer"
                value={selectedDeveloper}
                onChange={(value: string) => {
                  setSelectedDeveloper(value || null);
                  setPage(1);
                }}
                allowClear
                onClear={() => {
                  setSelectedDeveloper(null);
                  setPage(1);
                }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children?.toString()?.toLowerCase() || "").includes(
                    input.toLowerCase(),
                  )
                }
              >
                {developers.map((dev) => (
                  <Select.Option key={dev._id} value={dev._id}>
                    {dev.username}
                  </Select.Option>
                ))}
              </Select>
            </Col>

            {/* Date Selectors - With original functionality */}
            <Col xs={24} sm={24} md={10} lg={10} xl={10}>
              <div className="flex flex-col sm:flex-row gap-2 w-full">
                <Select
                  style={{ width: "100%", marginLeft: "2vw", height: "33px" }}
                  value={selectedDay || undefined}
                  onChange={(value) => {
                    setSelectedDay(value || null);
                    setPage(1);
                  }}
                  placeholder={
                    isCurrentMonthYear
                      ? dayjs().date().toString()
                      : "Select Day"
                  }
                  allowClear
                  onClear={() => {
                    if (isCurrentMonthYear) {
                      setSelectedDay(dayjs().date());
                    } else {
                      setSelectedDay(null);
                    }
                    setPage(1);
                  }}
                  options={Array.from(
                    {
                      length: dayjs(
                        `${selectedYear}-${selectedMonthNum}`,
                      ).daysInMonth(),
                    },
                    (_, i) => {
                      const day = i + 1;
                      const isToday =
                        day === dayjs().date() &&
                        selectedMonthNum === dayjs().month() + 1 &&
                        selectedYear === dayjs().year();
                      return {
                        value: day,
                        label: isToday ? `${day} ` : day.toString(),
                      };
                    },
                  )}
                />

                <Select
                  style={{ width: "100%", marginLeft: "2vw", height: "33px" }}
                  value={selectedMonthNum}
                  onChange={(value) => {
                    setSelectedMonthNum(value);
                    setPage(1);
                    if (
                      !(
                        value === dayjs().month() + 1 &&
                        selectedYear === dayjs().year()
                      )
                    ) {
                      setSelectedDay(null);
                    }
                  }}
                  options={[
                    { value: 1, label: "January" },
                    { value: 2, label: "February" },
                    { value: 3, label: "March" },
                    { value: 4, label: "April" },
                    { value: 5, label: "May" },
                    { value: 6, label: "June" },
                    { value: 7, label: "July" },
                    { value: 8, label: "August" },
                    { value: 9, label: "September" },
                    { value: 10, label: "October" },
                    { value: 11, label: "November" },
                    { value: 12, label: "December" },
                  ]}
                />

                <Select
                  style={{ width: "100%", marginLeft: "2vw", height: "33px" }}
                  value={selectedYear}
                  onChange={(value) => {
                    setSelectedYear(value);
                    setPage(1);
                    if (
                      !(
                        selectedMonthNum === dayjs().month() + 1 &&
                        value === dayjs().year()
                      )
                    ) {
                      setSelectedDay(null);
                    }
                  }}
                  options={Array.from({ length: 20 }, (_, i) => {
                    const year = dayjs().year() - 10 + i;
                    return { value: year, label: year.toString() };
                  })}
                />
              </div>
            </Col>

            {/* Export Button - Right-aligned */}
            <Col xs={24} sm={24} md={4} lg={6} xl={7}>
              <div className="flex justify-end w-full">
                <button
                  onClick={() => exportToExcel()}
                  className="min-w-[100px]  p-2 rounded text-center flex items-center justify-center"
                  style={{
                    marginLeft: "1vw",
                    backgroundColor: "#001529",
                    color: "white",
                    border: "none",
                    cursor: "pointer",
                    height: "33px",
                  }}
                  disabled={exportLoading}
                >
                  {exportLoading ? "Exporting..." : "Export Data"}
                </button>
              </div>
            </Col>
          </>
        )}

        {/* Developer View (roleId !== 1) - With original functionality */}
        {authData?.roleId?.roleId !== 1 && (
          <>
            {/* Action Buttons - Centered */}
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <div className="flex justify-center gap-2 flex-wrap">
                <Button
                  onClick={() => handleClockAction("clockIn")}
                  disabled={buttonState.clockIn}
                  className="min-w-[100px]"
                  style={{
                    backgroundColor: buttonState.clockIn
                      ? "#4B5563"
                      : "#001529",
                    color: "white",
                    cursor: buttonState.clockIn ? "not-allowed" : "pointer",
                    opacity: buttonState.clockIn ? 0.7 : 1,
                    border: "none",
                  }}
                >
                  Clock In
                </Button>

                <Button
                  onClick={() => handleClockAction("lunchStart")}
                  disabled={buttonState.lunchStart}
                  className="min-w-[100px]"
                  style={{
                    backgroundColor: buttonState.lunchStart
                      ? "#4B5563"
                      : "#001529",
                    color: "white",
                    cursor: buttonState.lunchStart ? "not-allowed" : "pointer",
                    opacity: buttonState.lunchStart ? 0.7 : 1,
                    border: "none",
                  }}
                >
                  Lunch Start
                </Button>

                <Button
                  onClick={() => handleClockAction("lunchEnd")}
                  disabled={buttonState.lunchComplete || (!isExcludedFromBreak && breakMinutesElapsed < MANDATORY_BREAK_MINUTES)}
                  className="min-w-[100px]"
                  style={{
                    backgroundColor:
                      buttonState.lunchComplete ||
                      (!isExcludedFromBreak && breakMinutesElapsed < MANDATORY_BREAK_MINUTES)
                        ? "#4B5563"
                        : "#001529",
                    color: "white",
                    cursor:
                      buttonState.lunchComplete ||
                      (!isExcludedFromBreak && breakMinutesElapsed < MANDATORY_BREAK_MINUTES)
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      buttonState.lunchComplete ||
                      (!isExcludedFromBreak && breakMinutesElapsed < MANDATORY_BREAK_MINUTES)
                        ? 0.7
                        : 1,
                    border: "none",
                  }}
                >
                  {(!isExcludedFromBreak && breakMinutesElapsed < MANDATORY_BREAK_MINUTES) && !buttonState.lunchComplete
                    ? `Lunch End (${MANDATORY_BREAK_MINUTES - breakMinutesElapsed}m left)`
                    : "Lunch End"}
                </Button>

                <Button
                  danger
                  onClick={showClockOutModal}
                  disabled={buttonState.clockOut}
                  className="min-w-[100px]"
                  style={{
                    backgroundColor: buttonState.clockOut
                      ? "#4B5563"
                      : "#001529",
                    color: "white",
                    cursor: buttonState.clockOut ? "not-allowed" : "pointer",
                    opacity: buttonState.clockOut ? 0.7 : 1,
                    border: "none",
                  }}
                >
                  Clock Out
                </Button>
              </div>
            </Col>

            {/* Date Selectors - Right-aligned */}
            <Col xs={24} sm={24} md={12} lg={8} xl={8}>
              <div className="flex flex-col sm:flex-row gap-2 w-full justify-end">
                <Select
                  style={{ width: "100%", maxWidth: 120 }}
                  value={selectedMonthNum}
                  onChange={(value) => {
                    setSelectedMonthNum(value);
                    setPage(1);
                    if (
                      !(
                        value === dayjs().month() + 1 &&
                        selectedYear === dayjs().year()
                      )
                    ) {
                      setSelectedDay(null);
                    }
                  }}
                  options={[
                    { value: 1, label: "Jan" },
                    { value: 2, label: "Feb" },
                    { value: 3, label: "Mar" },
                    { value: 4, label: "Apr" },
                    { value: 5, label: "May" },
                    { value: 6, label: "Jun" },
                    { value: 7, label: "Jul" },
                    { value: 8, label: "Aug" },
                    { value: 9, label: "Sep" },
                    { value: 10, label: "Oct" },
                    { value: 11, label: "Nov" },
                    { value: 12, label: "Dec" },
                  ]}
                />

                <Select
                  style={{ width: "100%", maxWidth: 100 }}
                  value={selectedYear}
                  onChange={(value) => {
                    setSelectedYear(value);
                    setPage(1);
                    if (
                      !(
                        selectedMonthNum === dayjs().month() + 1 &&
                        value === dayjs().year()
                      )
                    ) {
                      setSelectedDay(null);
                    }
                  }}
                  options={Array.from({ length: 5 }, (_, i) => {
                    const year = dayjs().year() - 2 + i;
                    return { value: year, label: year.toString() };
                  })}
                />
              </div>
            </Col>
          </>
        )}
      </Row>
      {/* Attendance Table */}
      <Row>
        <Col span={24}>
          <Card className="">
            <Spin spinning={loading}>
              <div
                className="scrollbar-hide custom-scroll-table"
                style={{ overflowX: "auto" }}
              >
                {" "}
                {/* ✅ Enables Horizontal Scrolling */}
                <Table
                  rowClassName={(record) =>
                    record.highlightRed ? "bg-red-100 text-red-700" : ""
                  }
                  columns={columns}
                  dataSource={updatedData}
                  scroll={{ x: "max-content" }}
                  rowKey={(record) => record._id || record.date}
                  pagination={false}
                />
              </div>
            </Spin>

            <Col span={24} className="p-2">
              <Row
                className="justify-between"
                align="middle"
                style={{ marginTop: 16 }}
              >
                <Col span={12}>
                  <Select
                    value={pageSize.toString()}
                    onChange={(value) => {
                      setPageSize(Number(value));
                      setPage(1); // Reset to page 1 on size change
                    }}
                    options={pageSizeOptions.map((size) => ({
                      label: `${size} / page`,
                      value: size,
                    }))}
                    style={{ width: 120 }}
                  />
                </Col>
                <Col span={12} className="flex justify-end">
                  <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={totalRecords}
                    onChange={(pageNumber) => setPage(pageNumber)}
                    showSizeChanger={false} // size changer is handled manually
                  />
                </Col>
              </Row>
            </Col>
          </Card>
        </Col>
      </Row>

      {/* Clock Out Confirmation Modal */}

      <Modal
        title={
          <span style={{ fontWeight: 600, fontSize: 18 }}>
            Confirm Clock Out
          </span>
        }
        open={isModalVisible}
        footer={null} // custom footer instead of default one
        onCancel={handleModalCancel}
        centered
        width={500}
        // bodyStyle={{}}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
        }}
      >
        <div className="p-3">
          <p style={{ marginBottom: 4, fontWeight: 500 }}>
            Enter your end-of-day report before clocking out:
          </p>

          <ReactQuill
            theme="snow"
            value={taskReport}
            onChange={setTaskReport}
            placeholder="Write your report here..."
            style={{ height: "150px", marginBottom: "50px" }}
          />
        </div>

        <div className="footer-modals" style={{ textAlign: "right" }}>
          <Button onClick={handleModalCancel} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleClockOutWithReport}
            // disabled={!taskReport.trim()}
            loading={loading}
            style={{ backgroundColor: "#001529", borderColor: "#001529" }}
          >
            Submit & Clock Out
          </Button>
        </div>
      </Modal>

      {currentTask && (
        // In your parent component where you render EditCustomersModal:
        <Editattendancemodal
          visible={isEditModalVisible}
          customerId={currentTask?._id}
          onClose={() => setIsEditModalVisible(false)}
          onSuccess={() => {
            setIsEditModalVisible(false);
            fetchAttendanceData();
          }}
        />
      )}
    </div>
  );
};

export const ATTENDANCE_TABLE_COLUMNS = [
  "userId",
  "date",
  "clockIn",
  "lunchStart",
  "lunchEnd",
  "clockOut",
  "endTime",
  "status",
  "ipAddress",
];

export default AttendanceTable;




